import { useState, useEffect } from "react";
import {
  Sidebar,
  SidebarTrigger,
  SidebarHeader,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarFooter,
  SidebarMenuButton,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
} from "@/components/blocks/evo-sidebar";
import { Settings, ChevronRight } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

import { useFavorites } from "./hooks/useFavorites";
import { DatabaseSwitcher } from "./components/DatabaseSwitcher";
import { FavoritesSection } from "./components/FavoritesSection";
import { MenuItemWithContext } from "./components/MenuItemWithContext";
import type { LeftMenuProps, MenuItem } from "./types";
import { menuItems, databases, menuConfig } from "./config/menuItems";

/**
 * Left Menu Component (Modular Version)
 * 
 * Features:
 * - Collapsible sidebar (icon-only mode)
 * - Favorites system (disabled - only 9 pages, threshold is 10)
 * - Database/Environment switcher (disabled - company selector in header)
 * - Context menu for favoriting items
 * - Mobile responsive
 * - Keyboard shortcuts (Cmd/Ctrl + B)
 */
export function LeftMenuModular({ activePage, onPageChange }: LeftMenuProps) {
  const [selectedDatabase, setSelectedDatabase] = useState("production");
  const [activeItemId, setActiveItemId] = useState<string>("");
  const [openMenus, setOpenMenus] = useState<Record<string, boolean>>({
    "resources-menu": true, // Resources menu open by default
  });

  const favoritesEnabled = menuConfig.totalPages >= menuConfig.favoritesThreshold;
  const { favorites, toggleFavorite, isFavorite } = useFavorites(favoritesEnabled);

  const favoriteItems = menuItems.filter((item) => isFavorite(item.id));

  // Sync activeItemId with activePage prop
  useEffect(() => {
    // Map page names to their menu item IDs
    const pageToItemId: Record<string, string> = {
      'dashboard': 'main-dashboard',
      'projects': 'main-projects',
      'portfolio': 'main-portfolio',
      'directory': 'sub-directory',
      'resources': 'sub-resources',
      'utilisation': 'sub-utilisation',
      'wip-reporting': 'main-wip-reporting',
      'profitability': 'main-profitability',
      'budgets': 'main-budgets',
      'gantt': 'main-gantt',
      'settings': 'main-settings',
    };
    
    const itemId = pageToItemId[activePage];
    if (itemId) {
      setActiveItemId(itemId);
    }
  }, [activePage]);

  const handleItemClick = (page: string, itemId: string) => {
    setActiveItemId(itemId);
    onPageChange(page);
  };

  const toggleMenu = (menuId: string) => {
    setOpenMenus(prev => ({
      ...prev,
      [menuId]: !prev[menuId]
    }));
  };

  const renderMenuItem = (item: MenuItem, uniqueId: string, isFavoriteItem: boolean = false) => {
    const isActive = activeItemId === uniqueId;
    return (
      <MenuItemWithContext
        item={item}
        uniqueId={uniqueId}
        isActive={isActive}
        isFavoriteItem={isFavoriteItem}
        isFavorite={isFavorite(item.id)}
        onItemClick={handleItemClick}
        onToggleFavorite={toggleFavorite}
        favoritesEnabled={favoritesEnabled}
      />
    );
  };

  return (
    <Sidebar variant="floating" collapsible="icon" className="relative h-full">
      {/* Header */}
      <SidebarHeader>
        <div className="flex items-center gap-2">
          <SidebarTrigger />
          <h3 className="font-semibold truncate whitespace-nowrap max-w-[195px] group-data-[state=collapsed]:hidden text-neutral-700">
            Projects & Billing
          </h3>
        </div>
      </SidebarHeader>
      <div className="border-t border-neutral-200 mx-2 group-data-[state=collapsed]:show"></div>

      {/* Content */}
      <SidebarContent className="group-data-[state=collapsed]:items-center">
        {/* Favorites Section - Only shows if enabled (currently disabled: 9 pages < 10 threshold) */}
        {favoritesEnabled && (
          <FavoritesSection favoriteItems={favoriteItems} renderMenuItem={renderMenuItem} />
        )}

        {/* Main Navigation Section */}
        <SidebarGroup className="group-data-[state=collapsed]:items-center">
          <SidebarGroupContent className="group-data-[state=collapsed]:w-8">
            <SidebarMenu className="gap-0.5 group-data-[state=collapsed]:w-full [&_span]:transition-all [&_span]:duration-200 [&_span]:ease-out [&_span]:opacity-100 [&_span]:translate-x-0 group-data-[state=collapsed]:[&_span]:opacity-0 group-data-[state=collapsed]:[&_span]:-translate-x-2 group-data-[state=collapsed]:[&_span]:pointer-events-none">
              
              {/* Dashboard */}
              <SidebarMenuItem>
                {renderMenuItem(menuItems.find((item) => item.id === "dashboard")!, "main-dashboard")}
              </SidebarMenuItem>

              {/* Projects */}
              <SidebarMenuItem>
                {renderMenuItem(menuItems.find((item) => item.id === "projects")!, "main-projects")}
              </SidebarMenuItem>

              {/* Portfolio */}
              <SidebarMenuItem>
                {renderMenuItem(menuItems.find((item) => item.id === "portfolio")!, "main-portfolio")}
              </SidebarMenuItem>

              {/* Resources Menu (Collapsible with sub-items) */}
              {(() => {
                const resourcesMenu = menuItems.find((item) => item.id === "resources-menu")!;
                const ResourcesIcon = resourcesMenu.icon;
                const DirectoryIcon = resourcesMenu.children![0].icon;
                const ResourceAllocationIcon = resourcesMenu.children![1].icon;
                const UtilisationIcon = resourcesMenu.children![2].icon;
                
                return (
                  <Collapsible
                    open={openMenus["resources-menu"]}
                    onOpenChange={() => toggleMenu("resources-menu")}
                    className="group/collapsible"
                  >
                    <SidebarMenuItem>
                      <CollapsibleTrigger asChild>
                        <SidebarMenuButton tooltip="Resources">
                          <ResourcesIcon />
                          <span>Resources</span>
                          <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                        </SidebarMenuButton>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <SidebarMenuSub>
                          {/* Directory */}
                          <SidebarMenuSubItem>
                            <SidebarMenuSubButton
                              onClick={() => handleItemClick("directory", "sub-directory")}
                              data-active={activeItemId === "sub-directory"}
                              className={activeItemId === "sub-directory" ? "bg-sidebar-accent font-medium text-sidebar-accent-foreground" : ""}
                            >
                              <DirectoryIcon />
                              <span>Directory</span>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                          {/* Resource Allocation */}
                          <SidebarMenuSubItem>
                            <SidebarMenuSubButton
                              onClick={() => handleItemClick("resources", "sub-resources")}
                              data-active={activeItemId === "sub-resources"}
                              className={activeItemId === "sub-resources" ? "bg-sidebar-accent font-medium text-sidebar-accent-foreground" : ""}
                            >
                              <ResourceAllocationIcon />
                              <span>Resource Allocation</span>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                          {/* Utilisation */}
                          <SidebarMenuSubItem>
                            <SidebarMenuSubButton
                              onClick={() => handleItemClick("utilisation", "sub-utilisation")}
                              data-active={activeItemId === "sub-utilisation"}
                              className={activeItemId === "sub-utilisation" ? "bg-sidebar-accent font-medium text-sidebar-accent-foreground" : ""}
                            >
                              <UtilisationIcon />
                              <span>Utilisation</span>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                        </SidebarMenuSub>
                      </CollapsibleContent>
                    </SidebarMenuItem>
                  </Collapsible>
                );
              })()}

              {/* WIP Reporting */}
              <SidebarMenuItem>
                {renderMenuItem(menuItems.find((item) => item.id === "wip-reporting")!, "main-wip-reporting")}
              </SidebarMenuItem>

              {/* Profitability */}
              <SidebarMenuItem>
                {renderMenuItem(menuItems.find((item) => item.id === "profitability")!, "main-profitability")}
              </SidebarMenuItem>

              {/* Budgets & Variance */}
              <SidebarMenuItem>
                {renderMenuItem(menuItems.find((item) => item.id === "budgets")!, "main-budgets")}
              </SidebarMenuItem>

              {/* Gantt Charts */}
              <SidebarMenuItem>
                {renderMenuItem(menuItems.find((item) => item.id === "gantt")!, "main-gantt")}
              </SidebarMenuItem>


            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      {/* Footer */}
      <SidebarFooter>
        {/* Settings */}
        {menuConfig.showSettings && (
          <SidebarMenu className="gap-0.5 [&_span]:transition-all [&_span]:duration-200 [&_span]:ease-out [&_span]:opacity-100 [&_span]:translate-x-0">
            <SidebarMenuItem>
              <SidebarMenuButton
                tooltip="Settings"
                onClick={() => handleItemClick("settings", "main-settings")}
                isActive={activeItemId === "main-settings"}
              >
                <Settings />
                <span>Settings</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        )}

        {/* Database Switcher - Not used (company selector in header) */}
        {menuConfig.showDatabaseSwitcher && (
          <>
            <div className="border-t border-neutral-200 mb-2 mt-2 group-data-[state=collapsed]:hidden"></div>
            <DatabaseSwitcher
              databases={databases}
              selectedDatabase={selectedDatabase}
              onDatabaseChange={setSelectedDatabase}
              label="Database"
            />
          </>
        )}
      </SidebarFooter>
    </Sidebar>
  );
}
